'use client';

import { useState, useMemo, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useStories } from '@/contexts/stories/hooks/useStories';
import { useIllustrations } from '../../../hooks/useBooks';
import { useIllustrationMutations } from '../../../hooks/useBookMutations';
import type { Illustration, IllustrationStatus } from '../../../types';

type SortOption = 'date' | 'title' | 'status';
type FilterOption = 'all' | IllustrationStatus;

export interface IllustrationSummary {
  id: string;
  storyId: string;
  title: string;
  status: IllustrationStatus;
  language?: string;
  coverImageUrl?: string | null;
  createdAt: string;
  isFavorite: boolean;
  favoritedAt: string | null;
  generationProgress?: {
    total: number;
    generated: number;
  } | null;
}

interface UseIllustrationLibraryReturn {
  illustrations: IllustrationSummary[];
  totalIllustrations: number;
  loading: boolean;
  error: string | null;

  // Filters
  filter: FilterOption;
  setFilter: (filter: FilterOption) => void;
  storyFilter: string;
  setStoryFilter: (storyId: string) => void;
  sort: SortOption;
  setSort: (sort: SortOption) => void;
  sortOrder: 'asc' | 'desc';
  setSortOrder: (order: 'asc' | 'desc') => void;
  languageFilter: string;
  setLanguageFilter: (lang: string) => void;
  languageOptions: string[];

  // Favorites filter (URL-driven)
  onlyFavorites: boolean;
  setOnlyFavorites: (value: boolean) => void;

  // Actions
  deleteIllustration: (bookId: string) => Promise<boolean>;
  toggleFavorite: (bookId: string) => Promise<void>;
  refetch: () => void;
}

function computeProgress(illustration: Illustration): IllustrationSummary['generationProgress'] {
  if (!illustration.pages || illustration.pages.length === 0) return null;
  const total = illustration.pages.length;
  const generated = illustration.pages.filter((p) => p.imageStatus === 'ready').length;
  return { total, generated };
}

export function useIllustrationLibrary(familyId: string | null | undefined): UseIllustrationLibraryReturn {
  const [filter, setFilter] = useState<FilterOption>('all');
  const [storyFilter, setStoryFilter] = useState<string>('all');
  const [languageFilter, setLanguageFilter] = useState<string>('');
  const [sort, setSort] = useState<SortOption>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // URL-driven favorites filter
  const searchParams = useSearchParams();
  const router = useRouter();
  const onlyFavorites = searchParams.get('filter') === 'favorites';

  const setOnlyFavorites = useCallback(
    (value: boolean) => {
      const params = new URLSearchParams(searchParams.toString());
      if (value) {
        params.set('filter', 'favorites');
      } else {
        params.delete('filter');
      }
      router.replace(`?${params.toString()}`, { scroll: false });
    },
    [searchParams, router],
  );

  // Books have no language column of their own — a book's language is the language
  // of its source story. Build a storyId → language map from the family's stories
  // (already cached) so the grid can show/filter it, mirroring stories & audio.
  const { data: storiesData } = useStories(familyId);
  const storyLanguage = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of storiesData?.stories ?? []) {
      if (s.language) map.set(s.id, s.language);
    }
    return map;
  }, [storiesData]);

  const { data: rawIllustrations, isLoading: loading, error: queryError, refetch: refetchQuery } =
    useIllustrations(familyId, { onlyFavorites });
  const { deleteIllustration: deleteIllustrationMutation, toggleFavorite: toggleFavoriteMutation } = useIllustrationMutations(familyId);

  const illustrations = useMemo((): IllustrationSummary[] => {
    if (!rawIllustrations) return [];

    let filtered: IllustrationSummary[] = rawIllustrations.map((illustration) => ({
      id: illustration.id,
      storyId: illustration.storyId,
      title: illustration.title,
      status: illustration.status,
      language: storyLanguage.get(illustration.storyId),
      coverImageUrl: illustration.coverImageUrl,
      createdAt: illustration.createdAt,
      isFavorite: illustration.isFavorite,
      favoritedAt: illustration.favoritedAt,
      generationProgress: computeProgress(illustration),
    }));

    // Apply status filter
    if (filter !== 'all') {
      filtered = filtered.filter((b) => b.status === filter);
    }

    // Apply story filter
    if (storyFilter !== 'all') {
      filtered = filtered.filter((b) => b.storyId === storyFilter);
    }

    // Apply language filter
    if (languageFilter) {
      filtered = filtered.filter((b) => b.language === languageFilter);
    }

    // Apply sort
    const direction = sortOrder === 'desc' ? -1 : 1;

    switch (sort) {
      case 'date':
        filtered.sort(
          (a, b) => direction * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
        );
        break;
      case 'title':
        filtered.sort((a, b) => direction * a.title.localeCompare(b.title));
        break;
      case 'status': {
        const statusOrder: Record<IllustrationStatus, number> = {
          analyzing: 0,
          ready: 1,
          generating: 2,
          draft: 3,
          failed: 4,
        };
        filtered.sort(
          (a, b) => direction * (statusOrder[a.status] - statusOrder[b.status]),
        );
        break;
      }
    }

    return filtered;
  }, [rawIllustrations, filter, storyFilter, languageFilter, sort, sortOrder]);

  // Distinct languages present across the family's books (from the source story).
  const languageOptions = useMemo(
    () =>
      Array.from(
        new Set((rawIllustrations ?? []).map((b) => storyLanguage.get(b.storyId)).filter(Boolean) as string[]),
      ).sort(),
    [rawIllustrations, storyLanguage],
  );

  const deleteIllustration = useCallback(
    async (bookId: string): Promise<boolean> => {
      try {
        const result = await deleteIllustrationMutation.mutateAsync(bookId);
        return result.success;
      } catch {
        return false;
      }
    },
    [deleteIllustrationMutation],
  );

  const toggleFavorite = useCallback(
    async (bookId: string): Promise<void> => {
      await toggleFavoriteMutation.mutateAsync(bookId);
    },
    [toggleFavoriteMutation],
  );

  const refetch = useCallback(() => { refetchQuery(); }, [refetchQuery]);

  return {
    illustrations,
    totalIllustrations: rawIllustrations?.length ?? 0,
    loading,
    error: queryError ? queryError.message : null,
    filter,
    setFilter,
    storyFilter,
    setStoryFilter,
    sort,
    setSort,
    sortOrder,
    setSortOrder,
    languageFilter,
    setLanguageFilter,
    languageOptions,
    onlyFavorites,
    setOnlyFavorites,
    deleteIllustration,
    toggleFavorite,
    refetch,
  };
}
